import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { getLMSStudentName } from '../lib/scorm';

const CourseContext = createContext(null);

const NAME_KEY = 'sfdc_student_name';

function getStorageKey(name) {
  return `sfdc_course_progress_${name || 'default'}`;
}

function getBookmarksKey(name) {
  return `sfdc_bookmarks_${name || 'default'}`;
}

function loadProgress(name) {
  try {
    const stored = localStorage.getItem(getStorageKey(name));
    if (stored) return JSON.parse(stored);
  } catch (e) {
    console.error('Failed to load progress:', e);
  }
  return { completedSubtopics: [], completedQuizzes: {} };
}

function saveProgress(name, progress) {
  if (!name) return;
  try {
    localStorage.setItem(getStorageKey(name), JSON.stringify(progress));
  } catch (e) {
    console.error('Failed to save progress:', e);
  }
}

function loadBookmarks(name) {
  try {
    const stored = localStorage.getItem(getBookmarksKey(name));
    if (stored) return JSON.parse(stored);
  } catch (e) {
    console.error('Failed to load bookmarks:', e);
  }
  return [];
}

function saveBookmarks(name, bookmarks) {
  if (!name) return;
  try {
    localStorage.setItem(getBookmarksKey(name), JSON.stringify(bookmarks));
  } catch (e) {
    console.error('Failed to save bookmarks:', e);
  }
}

export function CourseProvider({ children }) {
  // Try to determine the name as early as possible
  let initialName = '';
  try {
    const lmsName = getLMSStudentName();
    if (lmsName) {
      initialName = lmsName;
      localStorage.setItem(NAME_KEY, lmsName);
    } else {
      initialName = localStorage.getItem(NAME_KEY) || '';
    }
  } catch {}

  const [studentName, setStudentNameState] = useState(initialName);
  const [completedSubtopics, setCompletedSubtopics] = useState(() => loadProgress(initialName).completedSubtopics);
  const [completedQuizzes, setCompletedQuizzes] = useState(() => loadProgress(initialName).completedQuizzes);
  const [bookmarks, setBookmarks] = useState(() => loadBookmarks(initialName));
  const [totalSubtopics, setTotalSubtopics] = useState(0);

  const setStudentName = useCallback((name) => {
    const trimmed = name.trim();
    if (trimmed && !studentName) {
      setStudentNameState(trimmed);
      try { localStorage.setItem(NAME_KEY, trimmed); } catch (e) { console.error(e); }
      // Reload progress for this newly set name
      const newProgress = loadProgress(trimmed);
      setCompletedSubtopics(newProgress.completedSubtopics);
      setCompletedQuizzes(newProgress.completedQuizzes);
      setBookmarks(loadBookmarks(trimmed));
    }
  }, [studentName]);

  useEffect(() => {
    if (studentName) {
      saveProgress(studentName, { completedSubtopics, completedQuizzes });
    }
  }, [studentName, completedSubtopics, completedQuizzes]);

  useEffect(() => {
    if (studentName) {
      saveBookmarks(studentName, bookmarks);
    }
  }, [studentName, bookmarks]);

  const markSubtopicComplete = useCallback((subtopicId) => {
    setCompletedSubtopics(prev => {
      if (prev.includes(subtopicId)) return prev;
      return [...prev, subtopicId];
    });
  }, []);

  const markQuizComplete = useCallback((sectionId, score, total) => {
    setCompletedQuizzes(prev => ({
      ...prev,
      [sectionId]: { score, total, passed: score >= Math.ceil(total / 2) }
    }));
  }, []);

  const isSubtopicComplete = useCallback((subtopicId) => {
    return completedSubtopics.includes(subtopicId);
  }, [completedSubtopics]);

  const isQuizComplete = useCallback((sectionId) => {
    return !!completedQuizzes[sectionId];
  }, [completedQuizzes]);

  const getSectionProgress = useCallback((sectionSubtopicIds) => {
    if (!sectionSubtopicIds || sectionSubtopicIds.length === 0) return 0;
    const completed = sectionSubtopicIds.filter(id => completedSubtopics.includes(id)).length;
    return Math.round((completed / sectionSubtopicIds.length) * 100);
  }, [completedSubtopics]);

  const getOverallProgress = useCallback(() => {
    if (totalSubtopics === 0) return 0;
    return Math.round((completedSubtopics.length / totalSubtopics) * 100);
  }, [completedSubtopics, totalSubtopics]);

  const resetProgress = useCallback(() => {
    setCompletedSubtopics([]);
    setCompletedQuizzes({});
    if (studentName) {
      localStorage.removeItem(getStorageKey(studentName));
    }
  }, [studentName]);

  const toggleBookmark = useCallback((subtopicId) => {
    setBookmarks(prev => {
      if (prev.includes(subtopicId)) {
        return prev.filter(id => id !== subtopicId);
      }
      return [...prev, subtopicId];
    });
  }, []);

  const isBookmarked = useCallback((subtopicId) => {
    return bookmarks.includes(subtopicId);
  }, [bookmarks]);

  return (
    <CourseContext.Provider value={{
      completedSubtopics,
      completedQuizzes,
      totalSubtopics,
      setTotalSubtopics,
      markSubtopicComplete,
      markQuizComplete,
      isSubtopicComplete,
      isQuizComplete,
      getSectionProgress,
      getOverallProgress,
      resetProgress,
      bookmarks,
      toggleBookmark,
      isBookmarked,
      studentName,
      setStudentName
    }}>
      {children}
    </CourseContext.Provider>
  );
}

export function useCourse() {
  const context = useContext(CourseContext);
  if (!context) throw new Error('useCourse must be used within CourseProvider');
  return context;
}
